import { supabase } from '../../config/supabase';

/**
 * Fetch jobs assigned to a specific user (designer or estimator)
 * @param {string} userId - The user ID
 * @param {string} userRole - The user role (designer/estimator)
 * @param {string} companyId - The company ID
 * @returns {Promise<Array>} Array of assigned jobs
 */
export const getMyAssignedJobs = async (userId, userRole, companyId) => {
    try {
        if (userRole === 'designer') {
            // First, get all job IDs where this user is a designer
            const { data: designerJobs, error: designerError } = await supabase
                .from('job_designers')
                .select('job_id')
                .eq('user_id', userId);

            if (designerError) throw designerError;

            const jobIds = designerJobs.map(d => d.job_id);

            if (jobIds.length === 0) {
                return [];
            }

            // Then fetch those jobs with all related data
            const { data, error } = await supabase
                .from('jobs')
                .select(`
                    *,
                    job_quotes(*),
                    job_designers(*),
                    job_estimators(*),
                    job_production(*),
                    job_deliveries(*)
                `)
                .eq('company_id', companyId)
                .eq('deleted', false)
                .in('id', jobIds)
                .order('created_at', { ascending: false });

            if (error) throw error;
            return data || [];

        } else if (userRole === 'estimator') {
            // First, get all job IDs where this user is an estimator
            const { data: estimatorJobs, error: estimatorError } = await supabase
                .from('job_estimators')
                .select('job_id')
                .eq('user_id', userId);

            if (estimatorError) throw estimatorError;

            const jobIds = estimatorJobs.map(e => e.job_id);

            if (jobIds.length === 0) {
                return [];
            }

            // Then fetch those jobs with all related data
            const { data, error } = await supabase
                .from('jobs')
                .select(`
                    *,
                    job_quotes(*),
                    job_designers(*),
                    job_estimators(*),
                    job_production(*),
                    job_deliveries(*)
                `)
                .eq('company_id', companyId)
                .eq('deleted', false)
                .in('id', jobIds)
                .order('created_at', { ascending: false });

            if (error) throw error;
            return data || [];
        }

        return [];
    } catch (error) {
        console.error('Error fetching my assigned jobs:', error);
        throw error;
    }
};

/**
 * Check if user's assignment is completed
 * @param {Object} job - Job object
 * @param {string} userId - User ID
 * @param {string} userRole - User role
 * @returns {boolean} Whether the user's assignment is completed
 */
export const isUserAssignmentCompleted = (job, userId, userRole) => {
    if (userRole === 'designer') {
        const userDesigners = job.job_designers?.filter(d => d.user_id === userId);
        if (!userDesigners || userDesigners.length === 0) return false;
        return userDesigners.every(d => d.is_completed);
    } else if (userRole === 'estimator') {
        const estimator = job.job_estimators?.find(e => e.user_id === userId);
        return estimator?.is_completed || false;
    }
    return false;
};

/**
 * Get user's assigned quote types (for designers)
 * @param {Object} job - Job object
 * @param {string} userId - User ID
 * @returns {Array} Array of assigned quotes with status
 */
export const getUserAssignedQuotes = (job, userId) => {
    const assignedQuotes = [];

    job.job_designers?.forEach(designer => {
        if (designer.user_id === userId) {
            assignedQuotes.push({
                type: designer.quote_type,
                isCompleted: designer.is_completed,
                isStarted: designer.is_started
            });
        }
    });

    return assignedQuotes;
};

/**
 * Subscribe to job updates (uses same subscription as assignments with triggers)
 * @param {string} companyId - The company ID
 * @param {Function} callback - Callback function for updates
 * @returns {Object} Supabase channel subscription
 */
export const subscribeToMyJobUpdates = (companyId, callback) => {
    const channel = supabase
        .channel(`my-jobs-changes-${companyId}`)
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'jobs',
                filter: `company_id=eq.${companyId}`
            },
            callback
        )
        .subscribe();

    return channel;
};